/**
 * Parsing the recap's Markdown.
 *
 * The recap generator is ours and is told to "return concise Markdown only",
 * so the dialect is bounded: ATX headings, bullet and numbered lists, bold,
 * italic, inline code, paragraphs. That is the whole language — a dependency
 * that handles tables, footnotes and raw HTML would be parsing inputs this
 * app never produces, and raw HTML in particular is exactly what must never
 * be rendered from model output.
 *
 * The parser returns data, not elements, for the same reason guide/parse.ts
 * does: the shape is testable without a DOM, and the component that maps it
 * to React stays too small to hide a bug in.
 */

export type Inline =
  | { kind: 'text'; text: string }
  | { kind: 'strong'; children: Inline[] }
  | { kind: 'em'; children: Inline[] }
  | { kind: 'code'; text: string };

export type Block =
  | { kind: 'heading'; level: 1 | 2 | 3 | 4; children: Inline[] }
  | { kind: 'paragraph'; children: Inline[] }
  | { kind: 'list'; ordered: boolean; items: Inline[][] };

const HEADING = /^(#{1,4})\s+(.*)$/;
const BULLET = /^\s*[-*•]\s+(.*)$/;
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/;

/** Earliest-match scan. `**` before `*`, or every bold reads as two italics. */
const INLINE = /(`[^`]+`)|(\*\*(.+?)\*\*)|(\*([^*]+?)\*)/;

export function parseInline(text: string): Inline[] {
  const out: Inline[] = [];
  let rest = text;
  for (;;) {
    const m = INLINE.exec(rest);
    if (!m) break;
    if (m.index > 0) out.push({ kind: 'text', text: rest.slice(0, m.index) });
    if (m[1] !== undefined) {
      out.push({ kind: 'code', text: m[1].slice(1, -1) });
    } else if (m[3] !== undefined) {
      out.push({ kind: 'strong', children: parseInline(m[3]) });
    } else {
      out.push({ kind: 'em', children: parseInline(m[5] ?? '') });
    }
    rest = rest.slice(m.index + m[0].length);
  }
  if (rest) out.push({ kind: 'text', text: rest });
  return out;
}

export function parseMarkdown(md: string): Block[] {
  const blocks: Block[] = [];
  // A paragraph accumulates until something that is not prose interrupts it.
  let para: string[] = [];

  const flush = (): void => {
    if (para.length === 0) return;
    blocks.push({ kind: 'paragraph', children: parseInline(para.join(' ')) });
    para = [];
  };

  for (const line of md.replace(/\r\n/g, '\n').split('\n')) {
    if (line.trim() === '') {
      flush();
      continue;
    }
    const h = HEADING.exec(line);
    if (h) {
      flush();
      blocks.push({
        kind: 'heading',
        level: (h[1] ?? '#').length as 1 | 2 | 3 | 4,
        children: parseInline((h[2] ?? '').trim()),
      });
      continue;
    }
    const bullet = BULLET.exec(line);
    const numbered = bullet ? null : NUMBERED.exec(line);
    if (bullet || numbered) {
      flush();
      const ordered = numbered !== null;
      const item = parseInline((bullet ?? numbered)?.[1] ?? '');
      const last = blocks[blocks.length - 1];
      // Consecutive items of the same kind are one list; a bullet list
      // touching a numbered one is two.
      if (last && last.kind === 'list' && last.ordered === ordered) {
        last.items.push(item);
      } else {
        blocks.push({ kind: 'list', ordered, items: [item] });
      }
      continue;
    }
    para.push(line.trim());
  }
  flush();
  return blocks;
}

/**
 * Drop a document-titling first heading.
 *
 * The recap opens with "# Session recap", and it renders inside a card whose
 * header already says exactly that — so the h1 is the document naming itself
 * to a reader who was just told its name. The content lives in the ##
 * sections; the h1 is chrome.
 */
export function withoutLeadingH1(md: string): string {
  const lines = md.replace(/\r\n/g, '\n').split('\n');
  const first = lines.findIndex((l) => l.trim() !== '');
  if (first === -1 || !/^#\s+/.test(lines[first] ?? '')) return md;
  return lines.slice(first + 1).join('\n');
}
